import React, { useEffect, useState, useCallback } from 'react';
import { okJson } from '../services/httpError';
import {
  Key, KeyRound, Lock, Unlock, DoorOpen, Home, Building2, Car, Shield,
  Plus, Edit2, Trash2, History, AlertTriangle, X, Search, Loader2,
} from 'lucide-react';
import Select from 'react-select';
import { condoApi, makeAbsolutePhotoUrl, fetchWithAuth } from '../services/api';
import { useCustomer } from '../contexts/CustomerContext';
import { useKeycloak } from '../contexts/KeycloakContext';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';
import { getBlockLabel, getUnitLabel, CondoType } from '../utils/terminology';
import { useConfirm } from '../components/ui/ConfirmDialog';

const KEY_ICON_MAP: Record<string, React.ComponentType<any>> = {
  Key, KeyRound, Lock, Unlock, DoorOpen, Home, Building2, Car, Shield,
};
const KEY_ICON_LABELS: Record<string, string> = {
  Key: 'Chave', KeyRound: 'Redonda', Lock: 'Cadeado', Unlock: 'Aberto',
  DoorOpen: 'Porta', Home: 'Casa', Building2: 'Prédio', Car: 'Carro', Shield: 'Escudo',
};

interface CurrentHolder {
  person_id?: string;
  person_name: string;
  person_photo_url?: string;
  taken_at: string;
}
interface KeyItem {
  id: string; title: string; icon: string;
  warning_message: string; is_blocked: boolean; is_active: boolean;
  current_holder: CurrentHolder | null;
}
interface KeyTransaction {
  id: string; key_id: string; person_id?: string; person_name: string;
  action: 'taken' | 'returned'; notes: string;
  performed_by_user_name: string; created_at: string;
  person_photo_url?: string;
}
interface PersonResult {
  id: string; name?: string; first_name?: string; last_name?: string;
  identity_photo_url?: string;
}
type SelectOpt = { value: string; label: string };

// ─── react-select styles (styles-only, no classNames conflict) ───────────────

const modalSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '38px',
    backgroundColor: '#374151',
    borderColor: state.isFocused ? '#3b82f6' : '#4b5563',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: '#6b7280' },
    borderRadius: '6px',
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }),
  menuList: (base: any) => ({ ...base, backgroundColor: '#1f2937', padding: '4px' }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#374151' : 'transparent',
    color: '#e5e7eb',
    fontSize: '0.875rem',
    borderRadius: '4px',
  }),
  singleValue: (base: any) => ({ ...base, color: '#f3f4f6', fontSize: '0.875rem' }),
  placeholder: (base: any) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  input: (base: any) => ({ ...base, color: '#f3f4f6', fontSize: '0.875rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: (base: any) => ({ ...base, color: '#9ca3af', padding: '4px', '&:hover': { color: '#d1d5db' } }),
  dropdownIndicator: (base: any) => ({ ...base, color: '#9ca3af', padding: '4px', '&:hover': { color: '#d1d5db' } }),
};

// ─── KeyFormModal ─────────────────────────────────────────────────────────────

function KeyFormModal({ initial, onClose, onSave }: {
  initial?: Partial<KeyItem>;
  onClose: () => void;
  onSave: (data: { title: string; icon: string; warning_message: string; is_blocked: boolean }) => Promise<void>;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? 'Key');
  const [warningMessage, setWarningMessage] = useState(initial?.warning_message ?? '');
  const [isBlocked, setIsBlocked] = useState(initial?.is_blocked ?? false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try { await onSave({ title: title.trim(), icon, warning_message: warningMessage, is_blocked: isBlocked }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-gray-100">{initial?.id ? 'Editar Chave' : 'Nova Chave'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-200"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Título *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Chave do Portão" maxLength={100} required />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Ícone</label>
            <div className="grid grid-cols-5 gap-1.5">
              {Object.entries(KEY_ICON_MAP).map(([name, IconComponent]) => (
                <button key={name} type="button" onClick={() => setIcon(name)} title={KEY_ICON_LABELS[name]}
                  className={cn('flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors',
                    icon === name ? 'border-blue-500 bg-blue-500/15 text-blue-400' : 'border-gray-700 hover:border-gray-600 text-gray-500 hover:text-gray-300')}>
                  <IconComponent className="w-4 h-4" />
                  <span className="text-[10px] truncate w-full text-center">{KEY_ICON_LABELS[name]}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Aviso (opcional)</label>
            <textarea value={warningMessage} onChange={e => setWarningMessage(e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              placeholder="Mensagem exibida no card" rows={2} />
          </div>
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-gray-200">Bloqueada</p>
              <p className="text-xs text-gray-500">Impede retiradas enquanto bloqueada</p>
            </div>
            <button type="button" onClick={() => setIsBlocked(b => !b)}
              className={cn('relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors cursor-pointer', isBlocked ? 'bg-red-500' : 'bg-gray-700')}
              role="switch" aria-checked={isBlocked}>
              <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200', isBlocked ? 'translate-x-4' : 'translate-x-0')} />
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:bg-gray-800">Cancelar</button>
            <button type="submit" disabled={saving || !title.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {initial?.id ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── TakeKeyModal ─────────────────────────────────────────────────────────────

interface Block { id: string; name: string; }
interface Unit  { id: string; number: string; block_id?: string; block?: { name: string }; street?: { id: string; name: string }; street_number?: string; }
interface Department { id: string; name: string; }
interface Street { id: string; name: string; }

function TakeKeyModal({ keyItem, customerId, customerType, condoType, onClose, onDone }: {
  keyItem: KeyItem; customerId: string;
  customerType: string; condoType?: CondoType;
  onClose: () => void; onDone: () => void;
}) {
  const isEmpresa = customerType === 'empresa';

  // All data loaded upfront
  const [blocks, setBlocks]       = useState<Block[]>([]);
  const [streets, setStreets]     = useState<Street[]>([]);
  const [allUnits, setAllUnits]   = useState<Unit[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Selection state
  const [selectedBlock, setSelectedBlock]   = useState<SelectOpt | null>(null);
  const [selectedUnit, setSelectedUnit]     = useState<SelectOpt | null>(null);
  const [selectedStreet, setSelectedStreet] = useState<SelectOpt | null>(null);
  const [selectedNumber, setSelectedNumber] = useState<SelectOpt | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOpt | null>(null);

  // Person search
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState<PersonResult[]>([]);
  const [selected, setSelected]   = useState<PersonResult | null>(null);
  const [notes, setNotes]         = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving]       = useState(false);

  const getPersonName = (p: PersonResult) => p.name ?? [p.first_name, p.last_name].filter(Boolean).join(' ');
  const isCondoOfHouses = streets.length > 0;

  // Load ALL data upfront on open
  useEffect(() => {
    if (isEmpresa) {
      condoApi.getDepartments(customerId)
        .then((res: any) => setDepartments(Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : []))
        .catch(() => {});
    } else {
      condoApi.getBlocks(customerId)
        .then((res: any) => {
          const data: Block[] = Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : [];
          setBlocks([...data].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true })));
        }).catch(() => {});
      condoApi.getStreets(customerId)
        .then((res: any) => {
          const data: Street[] = Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : [];
          setStreets(data.filter((s: any) => s.is_active !== false));
        }).catch(() => {});
      const base = (window as any).__REACT_APP_CONDO_MANAGER_URL__ || process.env.REACT_APP_CONDO_MANAGER_URL || '';
      fetchWithAuth(`${base}/api/v1/customers/${customerId}/units`)
        .then(r => okJson<any>(r, 'Erro ao carregar as unidades'))
        .then((data: any) => setAllUnits(Array.isArray(data) ? data : (data?.data ?? [])))
        .catch((err: any) => toast.error(err?.message || 'Erro ao carregar as unidades'));
    }
  }, [customerId, isEmpresa]);

  // Derived unit lists
  const unitsForBlock: SelectOpt[] = selectedBlock
    ? [...allUnits]
        .filter(u => u.block_id === selectedBlock.value)
        .sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true }))
        .map(u => ({ value: u.id, label: u.number }))
    : [];

  const streetFilteredUnits = selectedStreet ? allUnits.filter(u => u.street?.id === selectedStreet.value) : [];
  const availableNumbers: SelectOpt[] = Array.from(
    new Set(streetFilteredUnits.map(u => u.street_number ?? '').filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true })).map(n => ({ value: n, label: n }));

  // Cross-link: Rua + Número → auto-fill Quadra + Lote
  const handleNumberChange = (opt: SelectOpt | null) => {
    setSelectedNumber(opt);
    setSelected(null);
    if (opt && selectedStreet) {
      const match = streetFilteredUnits.find(u => u.street_number === opt.value);
      if (match) {
        const blk = blocks.find(b => b.id === match.block_id);
        if (blk) setSelectedBlock({ value: blk.id, label: blk.name });
        setSelectedUnit({ value: match.id, label: match.number });
      }
    } else if (!opt) {
      setSelectedUnit(null);
    }
  };

  // Cross-link: Quadra + Lote → auto-fill Rua + Número
  const handleUnitChange = (opt: SelectOpt | null) => {
    setSelectedUnit(opt);
    setSelected(null);
    if (opt) {
      const full = allUnits.find(u => u.id === opt.value);
      if (full?.street?.id) {
        setSelectedStreet({ value: full.street.id, label: full.street.name });
        setSelectedNumber(full.street_number ? { value: full.street_number, label: full.street_number } : null);
      }
    }
  };

  // Cross-link: Quadra → clear Lote and reset Rua/Número if they don't match
  const handleBlockChange = (opt: SelectOpt | null) => {
    setSelectedBlock(opt);
    setSelectedUnit(null);
    setSelected(null);
    if (!opt) { setSelectedStreet(null); setSelectedNumber(null); }
  };

  // Consolidated search — runs on query change (debounced) AND on filter changes
  useEffect(() => {
    const hasUnitFilter  = !!(selectedUnit?.value || selectedDepartment?.value);
    const hasBlockFilter = !!selectedBlock?.value;

    if (!query.trim() && !hasUnitFilter && !hasBlockFilter) {
      setResults([]);
      return;
    }

    const run = async () => {
      setSearching(true);
      try {
        const params: any = {};
        if (query.trim()) params.search = query.trim();
        if (!isEmpresa) {
          params.role_type = 'resident';
          if (selectedUnit?.value)       params.unit_id  = selectedUnit.value;
          else if (selectedBlock?.value) params.block_id = selectedBlock.value;
        } else if (selectedDepartment?.value) {
          params.role_type     = 'staff';
          params.department_id = selectedDepartment.value;
        }
        const res = await condoApi.getPeople(customerId, params);
        const people = res?.data?.data ?? res?.data ?? [];
        setResults(Array.isArray(people) ? people.slice(0, 15) : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    };

    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  }, [query, selectedUnit, selectedBlock, selectedDepartment, customerId, isEmpresa]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const personName = selected ? getPersonName(selected) : query.trim();
    if (!personName.trim()) return;
    setSaving(true);
    try {
      await condoApi.takeKey(customerId, keyItem.id, { person_id: selected?.id, person_name: personName, notes });
      toast.success('Chave retirada com sucesso');
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Erro ao registrar retirada');
    } finally { setSaving(false); }
  };

  const lbl = "block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5";
  const inputCls = "w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-gray-100">Retirar: {keyItem.title}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-200"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {/* Location filters */}
          {isEmpresa ? (
            <div>
              <label className={lbl}>Departamento</label>
              <Select
                options={departments.map(d => ({ value: d.id, label: d.name }))}
                value={selectedDepartment}
                onChange={opt => { setSelectedDepartment(opt as SelectOpt | null); setSelected(null); }}
                placeholder="Todos os departamentos..."
                isClearable isSearchable
                styles={modalSelectStyles}
                noOptionsMessage={() => 'Nenhum departamento encontrado'}
              />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Rua + Número — only for casa-type condos */}
              {isCondoOfHouses && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>Rua</label>
                    <Select
                      options={streets.map(s => ({ value: s.id, label: s.name }))}
                      value={selectedStreet}
                      onChange={opt => {
                        setSelectedStreet(opt as SelectOpt | null);
                        setSelectedNumber(null);
                        if (!opt) { setSelectedUnit(null); setSelectedBlock(null); }
                        setSelected(null);
                      }}
                      placeholder="Selecione uma rua..."
                      isClearable isSearchable
                      styles={modalSelectStyles}
                      noOptionsMessage={() => 'Nenhuma rua encontrada'}
                    />
                  </div>
                  <div>
                    <label className={lbl}>Número</label>
                    <Select
                      options={availableNumbers}
                      value={selectedNumber}
                      onChange={opt => handleNumberChange(opt as SelectOpt | null)}
                      placeholder="Número..."
                      isClearable isSearchable
                      isDisabled={!selectedStreet}
                      styles={modalSelectStyles}
                      noOptionsMessage={() => selectedStreet ? 'Nenhum número encontrado' : 'Selecione a rua primeiro'}
                    />
                  </div>
                </div>
              )}

              {/* Quadra + Lote — always shown */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>{getBlockLabel(condoType)}</label>
                  <Select
                    options={blocks.map(b => ({ value: b.id, label: b.name }))}
                    value={selectedBlock}
                    onChange={opt => handleBlockChange(opt as SelectOpt | null)}
                    placeholder="Selecione..."
                    isClearable isSearchable
                    styles={modalSelectStyles}
                    noOptionsMessage={() => 'Nenhum encontrado'}
                  />
                </div>
                <div>
                  <label className={lbl}>{getUnitLabel(condoType)}</label>
                  <Select
                    options={unitsForBlock}
                    value={selectedUnit}
                    onChange={opt => handleUnitChange(opt as SelectOpt | null)}
                    placeholder={isCondoOfHouses ? 'Lote...' : 'Unidade...'}
                    isClearable isSearchable
                    isDisabled={!selectedBlock}
                    styles={modalSelectStyles}
                    noOptionsMessage={() => selectedBlock ? 'Nenhuma unidade' : 'Selecione primeiro'}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Person search */}
          <div>
            <label className={lbl}>Pessoa *</label>

            {/* Selected person display */}
            {selected ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md">
                {selected.identity_photo_url ? (
                  <img src={makeAbsolutePhotoUrl(selected.identity_photo_url)} alt={getPersonName(selected)}
                    className="w-7 h-9 rounded-md object-cover flex-shrink-0 border border-gray-600" />
                ) : (
                  <div className="w-7 h-9 rounded-md bg-gray-600 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-300">
                    {getPersonName(selected).charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="flex-1 text-sm font-medium text-gray-100 truncate">{getPersonName(selected)}</span>
                <button type="button" onClick={() => { setSelected(null); setQuery(''); }}
                  className="p-1 rounded hover:bg-gray-600 text-gray-500 hover:text-gray-200 flex-shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className={`${inputCls} pl-9 pr-3`}
                  placeholder="Buscar por nome ou digitar livremente"
                  autoFocus />
                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-500" />}
              </div>
            )}

            {/* Dropdown results */}
            {results.length > 0 && !selected && (
              <ul className="mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                {results.map(p => {
                  const photoUrl = p.identity_photo_url ? makeAbsolutePhotoUrl(p.identity_photo_url) : null;
                  return (
                    <li key={p.id}>
                      <button type="button" onClick={() => { setSelected(p); setResults([]); setQuery(''); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-gray-600 border-b border-gray-600/50 last:border-0 text-left">
                        {photoUrl ? (
                          <img src={photoUrl} alt={getPersonName(p)}
                            className="w-6 h-8 rounded-md object-cover flex-shrink-0 border border-gray-600" />
                        ) : (
                          <div className="w-6 h-8 rounded-md bg-gray-600 flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-gray-300">
                            {getPersonName(p).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="truncate">{getPersonName(p)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className={lbl}>Observações (opcional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
              className={inputCls} placeholder="Ex: Para manutenção do elevador" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-600 text-gray-400 rounded-lg hover:bg-gray-800">Cancelar</button>
            <button type="submit" disabled={saving || (!selected && !query.trim())}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Confirmar Retirada
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── KeyHistoryModal ──────────────────────────────────────────────────────────

function KeyHistoryModal({ keyItem, customerId, lightTheme = false, onClose }: {
  keyItem: KeyItem; customerId: string; lightTheme?: boolean; onClose: () => void;
}) {
  const { openPersonDetail } = usePersonDetailContext();
  const [history, setHistory] = useState<KeyTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    condoApi.getKeyHistory(customerId, keyItem.id)
      .then((data: any) => setHistory(data?.history ?? []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [customerId, keyItem.id]);

  const fmtDate = (s: string) => {
    try { return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return s; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className={cn(
        'border rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]',
        lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700'
      )}>
        <div className={cn('flex items-center justify-between px-5 py-4 border-b flex-shrink-0',
          lightTheme ? 'border-gray-200' : 'border-gray-800')}>
          <h2 className={cn('text-sm font-semibold', lightTheme ? 'text-gray-800' : 'text-gray-100')}>
            Histórico: {keyItem.title}
          </h2>
          <button onClick={onClose}
            className={cn('p-1 rounded transition-colors',
              lightTheme ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800')}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className={cn('w-5 h-5 animate-spin', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
            </div>
          ) : history.length === 0 ? (
            <p className={cn('text-sm text-center py-8', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
              Nenhum registro encontrado
            </p>
          ) : (
            <div className="space-y-1.5">
              {history.map(tx => {
                const photoUrl = tx.person_photo_url ? makeAbsolutePhotoUrl(tx.person_photo_url) : null;
                const isTaken = tx.action === 'taken';
                return (
                  <div key={tx.id} className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border',
                    lightTheme
                      ? isTaken
                        ? 'bg-amber-50 border-amber-100'
                        : 'bg-emerald-50/60 border-emerald-100'
                      : 'bg-gray-800 border-gray-700/60'
                  )}>
                    {/* Avatar */}
                    {photoUrl ? (
                      <img src={photoUrl} alt={tx.person_name}
                        className={cn('w-7 h-9 rounded-md object-cover flex-shrink-0 border',
                          lightTheme ? 'border-gray-200' : 'border-gray-700')} />
                    ) : (
                      <div className={cn(
                        'w-7 h-9 rounded-md flex items-center justify-center flex-shrink-0 text-xs font-bold',
                        lightTheme
                          ? isTaken ? 'bg-amber-200 text-amber-700' : 'bg-emerald-200 text-emerald-700'
                          : 'bg-gray-700 text-gray-300'
                      )}>
                        {tx.person_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Action label */}
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0',
                          isTaken ? 'bg-amber-500' : 'bg-emerald-500')} />
                        <span className={cn('text-[11px] font-medium',
                          lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                          {isTaken ? 'Retirada por' : 'Devolvida por'}
                        </span>
                      </div>
                      {/* Person name */}
                      {tx.person_id ? (
                        <button onClick={() => openPersonDetail(tx.person_id!, false, true)}
                          className={cn('text-sm font-semibold hover:text-blue-500 truncate block text-left',
                            lightTheme ? 'text-gray-800' : 'text-gray-100')}>
                          {tx.person_name}
                        </button>
                      ) : (
                        <span className={cn('text-sm font-semibold truncate block',
                          lightTheme ? 'text-gray-800' : 'text-gray-100')}>
                          {tx.person_name}
                        </span>
                      )}
                      {/* Date + performed by */}
                      <div className={cn('flex items-center gap-2 mt-1 text-[11px]',
                        lightTheme ? 'text-gray-500' : 'text-gray-600')}>
                        <span>{fmtDate(tx.created_at)}</span>
                        {tx.performed_by_user_name && (
                          <>
                            <span className="text-gray-400">·</span>
                            <span className={cn('font-medium',
                              lightTheme ? 'text-gray-600' : 'text-gray-500')}>
                              Registrado por: {tx.performed_by_user_name}
                            </span>
                          </>
                        )}
                      </div>
                      {tx.notes && (
                        <p className={cn('text-[11px] mt-0.5 italic',
                          lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                          "{tx.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Keys({ lightTheme = false, customerId: customerIdProp }: { lightTheme?: boolean; customerId?: string | null }) {
  const { selectedCustomer, availableCustomers } = useCustomer();
  const { hasRole } = useKeycloak();
  const { openPersonDetail } = usePersonDetailContext();
  const confirm = useConfirm();
  const [keys, setKeys] = useState<KeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState<KeyItem | null>(null);
  const [takingKey, setTakingKey] = useState<KeyItem | null>(null);
  const [historyKey, setHistoryKey] = useState<KeyItem | null>(null);

  // When rendered inside OperationalView, use the active tab's customer ID.
  // Fall back to selectedCustomer for standalone /keys route usage.
  const customerId = customerIdProp || (selectedCustomer?.id ?? '');
  const activeCustomer = availableCustomers.find((c: any) => c.id === customerId) ?? selectedCustomer;

  const isAdmin      = hasRole('admin') || hasRole('super_admin') || hasRole('supervisor') || hasRole('sindico') ||
    (hasRole('porteiro') && !!(activeCustomer as any)?.porteiro_can_manage_keys);
  const customerType = (activeCustomer as any)?.type ?? (activeCustomer as any)?.customer_type ?? 'condominio';
  const condoType: CondoType = (activeCustomer as any)?.condoType ?? (activeCustomer as any)?.condo_data?.condo_type ?? 'apartamento';

  const loadKeys = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const data = await condoApi.listKeys(customerId);
      setKeys(data?.keys ?? []);
    } catch { toast.error('Erro ao carregar chaves'); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async (formData: any) => {
    await condoApi.createKey(customerId, formData);
    toast.success('Chave criada'); setShowForm(false); loadKeys();
  };
  const handleUpdate = async (formData: any) => {
    if (!editingKey) return;
    await condoApi.updateKey(customerId, editingKey.id, formData);
    toast.success('Chave atualizada'); setEditingKey(null); loadKeys();
  };
  const handleDelete = async (key: KeyItem) => {
    if (!await confirm({ message: `Excluir "${key.title}"?`, variant: 'danger', confirmLabel: 'Excluir' })) return;
    try { await condoApi.deleteKey(customerId, key.id); toast.success('Chave excluída'); loadKeys(); }
    catch { toast.error('Erro ao excluir'); }
  };
  const handleReturn = async (key: KeyItem) => {
    try { await condoApi.returnKey(customerId, key.id); toast.success('Chave devolvida'); loadKeys(); }
    catch (err: any) { toast.error(err?.response?.data?.message ?? 'Erro ao devolver'); }
  };
  const handleToggleBlock = async (key: KeyItem) => {
    try {
      await condoApi.updateKey(customerId, key.id, { is_blocked: !key.is_blocked });
      toast.success(key.is_blocked ? 'Desbloqueada' : 'Bloqueada'); loadKeys();
    } catch { toast.error('Erro'); }
  };

  const [keySearch, setKeySearch] = useState('');

  const fmtDate = (s: string) => {
    try { return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return s; }
  };

  const filteredKeys = keySearch.trim()
    ? keys.filter(k => k.title.toLowerCase().includes(keySearch.toLowerCase()))
    : keys;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-widest">Chaves</h2>
          {!loading && keys.length > 0 && (
            <span className="text-[11px] text-gray-600">({keys.length})</span>
          )}
          {loading && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
        </div>
        {!loading && keys.length > 0 && (
          <div className="relative flex-1 min-w-0">
            <Search className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
            <input
              type="text"
              value={keySearch}
              onChange={e => setKeySearch(e.target.value)}
              placeholder="Buscar chave..."
              className={cn(
                'w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border outline-none',
                lightTheme
                  ? 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-blue-400'
                  : 'bg-gray-900 border-gray-800 text-gray-200 placeholder-gray-600 focus:border-gray-600',
              )}
            />
          </div>
        )}
        {isAdmin && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0">
            <Plus className="h-3.5 w-3.5" /> Nova Chave
          </button>
        )}
      </div>

      {/* ── Cards ───────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
        </div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center mb-3 border',
            lightTheme ? 'bg-gray-100 border-gray-200' : 'bg-gray-900 border-gray-800')}>
            <KeyRound className={cn('w-5 h-5', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
          </div>
          <p className={cn('text-sm font-medium', lightTheme ? 'text-gray-500' : 'text-gray-500')}>Nenhuma chave cadastrada</p>
          {isAdmin && (
            <button onClick={() => setShowForm(true)}
              className="mt-3 text-xs text-blue-500 hover:text-blue-400 font-medium">
              + Cadastrar primeira chave
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {filteredKeys.length === 0 && keySearch.trim() ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className={cn('text-sm', lightTheme ? 'text-gray-400' : 'text-gray-500')}>Nenhuma chave encontrada</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-2">
          {filteredKeys.map(key => {
            const IconComponent = KEY_ICON_MAP[key.icon] ?? Key;
            const isTaken   = !!key.current_holder;
            const isBlocked = key.is_blocked;
            const photoUrl  = key.current_holder?.person_photo_url
              ? makeAbsolutePhotoUrl(key.current_holder.person_photo_url)
              : null;

            return (
              <div key={key.id}
                className={cn(
                  'relative flex flex-col rounded-xl overflow-hidden transition-colors border',
                  lightTheme
                    ? isBlocked
                      ? 'bg-red-50 border-red-200 hover:border-red-300'
                      : isTaken
                        ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                        : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
                    : isBlocked
                      ? 'bg-gray-900 border-red-900/60 hover:border-red-800/60'
                      : isTaken
                        ? 'bg-gray-900 border-amber-900/50 hover:border-amber-800/50'
                        : 'bg-gray-900 border-gray-800 hover:border-gray-700'
                )}>

                {/* Top accent line */}
                <div className={cn('h-0.5 w-full',
                  isBlocked ? 'bg-red-500' : isTaken ? 'bg-amber-400' : lightTheme ? 'bg-gray-300' : 'bg-gray-700/60')} />

                <div className="p-4 flex flex-col gap-3 flex-1">
                  {/* Title row */}
                  <div className="flex items-start gap-2.5">
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5',
                      isBlocked
                        ? 'bg-red-500/10'
                        : isTaken
                          ? 'bg-amber-500/10'
                          : lightTheme ? 'bg-gray-100' : 'bg-gray-800'
                    )}>
                      <IconComponent className={cn('w-4 h-4',
                        isBlocked ? 'text-red-500' : isTaken ? 'text-amber-500' : lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm font-semibold leading-snug',
                        lightTheme ? 'text-gray-800' : 'text-gray-100')}>{key.title}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold',
                          isBlocked
                            ? 'bg-red-500/10 text-red-500'
                            : isTaken
                              ? 'bg-amber-500/10 text-amber-600'
                              : lightTheme
                                ? 'bg-emerald-500/10 text-emerald-600'
                                : 'bg-emerald-500/10 text-emerald-400'
                        )}>
                          <span className={cn('w-1 h-1 rounded-full',
                            isBlocked ? 'bg-red-500' : isTaken ? 'bg-amber-500' : 'bg-emerald-500')} />
                          {isBlocked ? 'Bloqueada' : isTaken ? 'Em posse' : 'Disponível'}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => setHistoryKey(key)}
                      className={cn('p-1 rounded flex-shrink-0 transition-colors',
                        lightTheme
                          ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                          : 'text-gray-700 hover:text-gray-400 hover:bg-gray-800')}
                      title="Histórico">
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Holder */}
                  {isTaken && (
                    <div className={cn(
                      'flex items-center gap-2 px-2.5 py-2 rounded-lg border',
                      lightTheme
                        ? 'bg-amber-100/60 border-amber-200'
                        : 'bg-gray-800/60 border-gray-700/40'
                    )}>
                      {photoUrl ? (
                        <img src={photoUrl} alt={key.current_holder!.person_name}
                          className={cn('w-6 h-8 rounded-md object-cover flex-shrink-0 border',
                            lightTheme ? 'border-amber-200' : 'border-gray-700')} />
                      ) : (
                        <div className={cn(
                          'w-6 h-8 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-bold',
                          lightTheme ? 'bg-amber-200 text-amber-700' : 'bg-gray-700 text-gray-300'
                        )}>
                          {key.current_holder!.person_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        {key.current_holder!.person_id ? (
                          <button onClick={() => openPersonDetail(key.current_holder!.person_id!, false, true)}
                            className={cn('text-xs font-semibold hover:text-blue-500 truncate block text-left w-full leading-snug',
                              lightTheme ? 'text-gray-800' : 'text-gray-100')}>
                            {key.current_holder!.person_name}
                          </button>
                        ) : (
                          <span className={cn('text-xs font-semibold truncate block leading-snug',
                            lightTheme ? 'text-gray-800' : 'text-gray-100')}>
                            {key.current_holder!.person_name}
                          </span>
                        )}
                        {key.current_holder!.taken_at && (
                          <span className={cn('text-[10px]', lightTheme ? 'text-amber-600/70' : 'text-gray-500')}>
                            {fmtDate(key.current_holder!.taken_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Warning */}
                  {key.warning_message && (
                    <div className="flex items-start gap-1.5 px-2.5 py-1.5 bg-amber-500/10 border border-amber-400/25 rounded-lg">
                      <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className={cn('text-[11px] leading-snug', lightTheme ? 'text-amber-700' : 'text-amber-400/80')}>
                        {key.warning_message}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 mt-auto pt-1">
                    {!isTaken && !isBlocked && (
                      <button onClick={() => setTakingKey(key)}
                        className="flex-1 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors">
                        Retirar
                      </button>
                    )}
                    {isTaken && (
                      <button onClick={() => handleReturn(key)}
                        className={cn(
                          'flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors',
                          lightTheme
                            ? 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
                            : 'text-gray-200 bg-gray-700/80 hover:bg-gray-600 border-gray-600/60'
                        )}>
                        Devolver
                      </button>
                    )}
                    {isBlocked && !isTaken && (
                      <div className="flex-1 py-1.5 text-xs font-semibold text-red-400/60 text-center select-none">
                        Bloqueada
                      </div>
                    )}
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleToggleBlock(key)}
                          className={cn('p-1.5 rounded-md transition-colors',
                            isBlocked
                              ? 'text-red-500 hover:bg-red-500/10'
                              : lightTheme
                                ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                : 'text-gray-600 hover:text-gray-400 hover:bg-gray-800')}
                          title={isBlocked ? 'Desbloquear' : 'Bloquear'}>
                          {isBlocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setEditingKey(key)}
                          className={cn('p-1.5 rounded-md transition-colors',
                            lightTheme
                              ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                              : 'text-gray-600 hover:text-gray-400 hover:bg-gray-800')}
                          title="Editar">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(key)}
                          className={cn('p-1.5 rounded-md transition-colors',
                            lightTheme
                              ? 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              : 'text-gray-700 hover:text-red-400 hover:bg-red-500/10')}
                          title="Excluir">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          )}
        </div>
      )}

      {showForm    && <KeyFormModal onClose={() => setShowForm(false)} onSave={handleCreate} />}
      {editingKey  && <KeyFormModal initial={editingKey} onClose={() => setEditingKey(null)} onSave={handleUpdate} />}
      {takingKey   && (
        <TakeKeyModal keyItem={takingKey} customerId={customerId}
          customerType={customerType} condoType={condoType}
          onClose={() => setTakingKey(null)}
          onDone={() => { setTakingKey(null); loadKeys(); }} />
      )}
      {historyKey  && <KeyHistoryModal keyItem={historyKey} customerId={customerId} lightTheme={lightTheme} onClose={() => setHistoryKey(null)} />}
    </div>
  );
}
